import { useCallback, useEffect, useRef, useState } from 'react';
import { useRelayEnvironment, fetchQuery } from 'react-relay';
import { createOperationDescriptor, getRequest } from 'relay-runtime';
import { isNotNilOrEmpty } from 'ramda-adjunct';
import { equals, isNil, prop, propOr, isNotNil } from 'ramda';
import { unstable_batchedUpdates } from 'react-dom';
import { QCacheQuery } from '../_graphql/queries/QCache';
import UpdateCacheMutation from '../_graphql/mutations/UpdateCacheMutation';

const openDatabase = (dbName, version) => {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(dbName, version);

    request.onerror = (event) => {
      reject(event.target.error);
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      db.createObjectStore('cache');
    };
  });
};

export const useFetchQuery = ({
  query,
  args,
  skip = false,
  defaultData = {},
  dataProp,
  cacheKey,
  staleWhileRevalidate = false,
  subscribeToStore = false,
  env
}) => {
  const relayEnv = useRelayEnvironment();
  const environment = env || relayEnv;
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resultData, setResultData] = useState(defaultData);
  const [forceFetch, setForceFetch] = useState(false);
  const isMountedRef = useRef(true);
  const storeSubscriptionRef = useRef(null);
  const isUnmountedRef = useRef(false);

  const reFetch = useCallback(() => setForceFetch(true), []);

  const disposeStoreSubscription = () => {
    const storeSubscription = storeSubscriptionRef.current;
    if (isNil(storeSubscription)) return;
    storeSubscription.subscription.dispose();
    storeSubscription.retain.dispose();
    storeSubscriptionRef.current = null;
  };

  // Keeps resultData in sync with the Relay store so that mutation updaters are reflected in the UI
  const subscribeToStoreUpdates = () => {
    disposeStoreSubscription();
    // the request may resolve after unmount: subscribing then would leak
    if (isUnmountedRef.current) return;
    const operation = createOperationDescriptor(getRequest(query), args);
    const retain = environment.retain(operation);
    const snapshot = environment.lookup(operation.fragment);
    const subscription = environment.subscribe(snapshot, ({ data }) => setResultData(data));
    storeSubscriptionRef.current = { retain, subscription };
  };

  useEffect(() => () => {
    isUnmountedRef.current = true;
    disposeStoreSubscription();
  }, []);

  const saveDataToCache = async (key, data, dateCache, actualDate) => {
    try {
      const db = await openDatabase(cacheKey[0], 1);
      const tx = db.transaction('cache', 'readwrite');
      const store = tx.objectStore('cache');
      store.put({ date: dateCache.toString(), data }, key);
      await tx.complete;
      if (equals(dateCache, actualDate)) UpdateCacheMutation({ date: dateCache, key }, () => {});
      db.close();
    } catch (error) {
      console.error('Error saving data to cache:', error);
    }
  };

  const getCachedDataFromDB = async (key) => {
    try {
      const db = await openDatabase(cacheKey[0], 1);
      const tx = db.transaction('cache', 'readonly');
      const store = tx.objectStore('cache');
      const request = await store.get(key);
      return new Promise((resolve, reject) => {
        request.onsuccess = (event) => {
          const data = event.target.result;
          db.close();
          resolve(data);
        };

        request.onerror = (event) => {
          reject(event.target.error);
        };
      });
    } catch (error) {
      console.error('Error getting cached data from cache:', error);
      return null;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const revalidate = staleWhileRevalidate && isMountedRef.current;
      setIsLoading(true);
      const actualDate = new Date();
      let dateCache = actualDate;
      if (cacheKey && !forceFetch) {
        const result = await getCachedDataFromDB(cacheKey[1]);
        const { cache: { date } } = await fetchQuery(environment, QCacheQuery, { key: cacheKey[1] }).toPromise();
        if (isNil(result) && isNotNil(date)) dateCache = new Date(date);
        if (isNotNilOrEmpty(prop('data', result)) && (isNil(date) || equals(new Date(date), new Date(prop('date', result))))) {
          unstable_batchedUpdates(() => {
            setResultData(prop('data', result));
            setIsLoading(false);
          });
          if (!revalidate) return;
        }
      }
      try {
        const data = await fetchQuery(environment, query, args).toPromise();
        if (cacheKey) await saveDataToCache(cacheKey[1], data, dateCache, actualDate);
        if (subscribeToStore) subscribeToStoreUpdates();
        unstable_batchedUpdates(() => {
          setResultData(data);
          setIsLoading(false);
          setForceFetch(false);
        });
      } catch (error) {
        unstable_batchedUpdates(() => {
          setErrorMsg(JSON.stringify(error, null, 4));
          setIsLoading(false);
        });
      }
    };

    if (skip && !forceFetch) {
      return () => {};
    } else {
      fetchData();
      return () => {
        isMountedRef.current = false;
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, forceFetch]);

  return {
    isLoading,
    error: errorMsg,
    data: resultData,
    [dataProp]: propOr(defaultData, dataProp, resultData),
    reFetch
  };
};
export default useFetchQuery;
